// API client for TCO backend
// This will be replaced with generated client from @tco/api-client package

export interface ApiResponse<T = any> {
  data: T;
  message?: string;
  status: 'success' | 'error';
}

export interface PaginatedResponse<T = any> {
  data: T[];
  page: number;
  limit: number;
  total: number;
  totalPages: number;
  activeCount?: number;
  from1cCount?: number;
  localCount?: number;
}

export interface ApiError {
  message: string;
  status: number;
  code?: string;
}


import AuthService from '../services/AuthService';
import envConfig from '../config/env';
import type { OneCSync_Response } from '../types/onec-sync.types';

// Base API configuration
const API_BASE_URL = envConfig.getApiBaseUrl();

class ApiClient {
  public baseURL: string;
  private authService: AuthService;

  constructor(baseURL: string = API_BASE_URL) {
    this.baseURL = baseURL;
    this.authService = AuthService.getInstance();
  }

  async getHeaders(): Promise<HeadersInit> {
    const token = await this.authService.getValidAccessToken();
    const tenantId = this.authService.getTenantId();

    return {
      'Content-Type': 'application/json',
      ...(token && { 'Authorization': `Bearer ${token}` }),
      ...(tenantId && { 'X-Tenant-Id': tenantId }),
    };
  }

  private buildQueryString(params?: Record<string, any>): string {
    if (!params) return '';

    const searchParams = new URLSearchParams();

    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        searchParams.append(key, String(value));
      }
    });

    return searchParams.toString();
  }

  private async request<T>(
    endpoint: string,
    options: RequestInit = {},
    isRetry: boolean = false
  ): Promise<T> {
    const url = `${this.baseURL}${endpoint}`;

    // Get authentication headers
    const token = await this.authService.getValidAccessToken();
    const tenantId = this.authService.getTenantId();

    // If we don't have a valid token or tenant ID, redirect to login
    // (except for public endpoints like /health)
    if (!endpoint.startsWith('/health') && (!token || !tenantId)) {
      console.warn('[ApiClient] No valid token or tenant ID available, redirecting to login');
      await this.authService.logout();
      window.location.href = '/login';
      throw {
        message: 'Session expired. Please login again.',
        status: 401,
      } as ApiError;
    }

    const headers: HeadersInit = {
      'Content-Type': 'application/json',
      ...(token && { 'Authorization': `Bearer ${token}` }),
      ...(tenantId && { 'X-Tenant-Id': tenantId }),
      ...options.headers,
    };

    const config: RequestInit = {
      ...options,
      headers,
    };

    try {
      const response = await fetch(url, config);

      if (!response.ok) {
        // Handle 401 unauthorized - try to refresh token and retry once
        if (response.status === 401) {
          // Only retry once to prevent infinite loops
          if (!isRetry) {
            console.warn('[ApiClient] Received 401, attempting to refresh token and retry...');

            try {
              // Force token refresh
              const newToken = await this.authService.getValidAccessToken();

              if (newToken) {
                console.log('[ApiClient] Token refreshed successfully, retrying request...');
                // Retry the request with the new token
                return await this.request<T>(endpoint, options, true);
              }
            } catch (refreshError) {
              console.error('[ApiClient] Token refresh failed:', refreshError);
              // Fall through to logout
            }
          } else {
            console.error('[ApiClient] Retry with refreshed token also failed with 401');
          }

          // If we're here, either retry failed or token refresh failed
          console.warn('[ApiClient] Session expired, redirecting to login');
          await this.authService.logout();
          window.location.href = '/login';
          throw {
            message: 'Session expired. Please login again.',
            status: 401,
          } as ApiError;
        }

        const errorData = await response.json().catch(() => ({}));
        console.error('API Error Response:', {
          status: response.status,
          errorData,
          url: response.url
        });
        console.error('Full error details:', JSON.stringify(errorData, null, 2));
        throw {
          message: errorData.message || `HTTP error ${response.status}`,
          status: response.status,
          code: errorData.code,
        } as ApiError;
      }

      // Для DELETE запросов с кодом 204 тело ответа пустое
      if (response.status === 204) {
        return null as unknown as T;
      }

      // Проверяем, есть ли контент в ответе
      const contentType = response.headers.get('content-type');
      if (contentType && contentType.includes('application/json')) {
        const data = await response.json();
        return data;
      } else {
        // Если контента нет или он не JSON, возвращаем пустой объект
        return {} as T;
      }
    } catch (error) {
      if (error instanceof TypeError) {
        throw {
          message: 'Network error - please check your connection',
          status: 0,
        } as ApiError;
      }
      throw error;
    }
  }

  // Health check
  async getHealth(): Promise<{ status: string; timestamp: string }> {
    return this.request('/health');
  }

  // Users API
  async getUsers(params?: Record<string, any>): Promise<PaginatedResponse<any>> {
    const searchParams = this.buildQueryString(params);
    return this.request(`/api/v1/users${searchParams ? `?${searchParams}` : ''}`);
  }

  async getUser(id: string): Promise<ApiResponse<any>> {
    return this.request(`/api/v1/users/${id}`);
  }

  async createUser(data: any): Promise<ApiResponse<any>> {
    return this.request('/api/v1/users', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateUser(id: string, data: any): Promise<ApiResponse<any>> {
    return this.request(`/api/v1/users/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  async deleteUser(id: string): Promise<ApiResponse<any> | null> {
    return this.request(`/api/v1/users/${id}`, {
      method: 'DELETE',
    });
  }

  async changePassword(currentPassword: string, newPassword: string): Promise<{ message: string }> {
    return this.request('/api/v1/users/me/change-password', {
      method: 'PATCH',
      body: JSON.stringify({
        currentPassword,
        newPassword,
      }),
    });
  }

  async resetUserPassword(userId: string, newPassword: string): Promise<{ message: string }> {
    return this.request(`/api/v1/users/${userId}/reset-password`, {
      method: 'PATCH',
      body: JSON.stringify({
        newPassword,
      }),
    });
  }

  // Employees API
  async getEmployees(params?: Record<string, any>): Promise<PaginatedResponse<any>> {
    const searchParams = this.buildQueryString(params);
    return this.request(`/api/v1/employees${searchParams ? `?${searchParams}` : ''}`);
  }

  async getEmployee(id: string): Promise<ApiResponse<any>> {
    return this.request(`/api/v1/employees/${id}`);
  }

  async createEmployee(data: any): Promise<any> {
    return this.request('/api/v1/employees', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateEmployee(id: string, data: any): Promise<ApiResponse<any>> {
    return this.request(`/api/v1/employees/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  async deleteEmployee(id: string): Promise<ApiResponse<any> | null> {
    return this.request(`/api/v1/employees/${id}`, {
      method: 'DELETE',
    });
  }

  async createInvitationForEmployee(employeeId: string, roleId: string): Promise<{
    invitation: {
      id: string;
      invite_code: string;
      expires_at: string;
      is_valid: boolean;
    };
  }> {
    return this.request(`/api/v1/employees/${employeeId}/create-invitation`, {
      method: 'POST',
      body: JSON.stringify({ role_id: roleId }),
    });
  }

  // Device Binding API
  async getBoundDevice(employeeId: string): Promise<{
    has_bound_device: boolean;
    device?: {
      id: string;
      device_id: string;
      device_name: string | null;
      platform: string;
      app_version: string | null;
      bound_at: string;
      last_used_at: string | null;
    };
  }> {
    return this.request(`/api/v1/employees/${employeeId}/bound-device`);
  }

  async resetDeviceBinding(employeeId: string): Promise<{
    success: boolean;
    message: string;
  }> {
    return this.request(`/api/v1/employees/${employeeId}/reset-device-binding`, {
      method: 'POST',
    });
  }

  // Departments API
  async getDepartments(params?: Record<string, any>): Promise<PaginatedResponse<any>> {
    const searchParams = this.buildQueryString(params);
    return this.request(`/api/v1/departments${searchParams ? `?${searchParams}` : ''}`);
  }

  async getDepartment(id: string): Promise<ApiResponse<any>> {
    return this.request(`/api/v1/departments/${id}`);
  }

  async createDepartment(data: any): Promise<ApiResponse<any>> {
    return this.request('/api/v1/departments', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateDepartment(id: string, data: any): Promise<ApiResponse<any>> {
    return this.request(`/api/v1/departments/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  async deleteDepartment(id: string): Promise<ApiResponse<any> | null> {
    return this.request(`/api/v1/departments/${id}`, {
      method: 'DELETE',
    });
  }

  // Positions API
  async getPositions(params?: Record<string, any>): Promise<PaginatedResponse<any>> {
    const searchParams = this.buildQueryString(params);
    return this.request(`/api/v1/positions${searchParams ? `?${searchParams}` : ''}`);
  }

  async getPosition(id: string): Promise<ApiResponse<any>> {
    return this.request(`/api/v1/positions/${id}`);
  }

  async createPosition(data: any): Promise<ApiResponse<any>> {
    return this.request('/api/v1/positions', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updatePosition(id: string, data: any): Promise<ApiResponse<any>> {
    return this.request(`/api/v1/positions/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  async deletePosition(id: string): Promise<ApiResponse<any> | null> {
    return this.request(`/api/v1/positions/${id}`, {
      method: 'DELETE',
    });
  }

  // Roles API
  async getRoles(params?: Record<string, any>): Promise<PaginatedResponse<any>> {
    const searchParams = this.buildQueryString(params);
    return this.request(`/api/v1/roles${searchParams ? `?${searchParams}` : ''}`);
  }

  async createRole(data: { name: string; permissions: Record<string, any> }): Promise<ApiResponse<any>> {
    return this.request('/api/v1/roles', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateRole(id: string, data: { name?: string; permissions?: Record<string, any> }): Promise<ApiResponse<any>> {
    return this.request(`/api/v1/roles/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  async deleteRole(id: string): Promise<ApiResponse<any> | null> {
    return this.request(`/api/v1/roles/${id}`, {
      method: 'DELETE',
    });
  }

  // Locations API
  async getLocations(params?: Record<string, any>): Promise<PaginatedResponse<any>> {
    const searchParams = this.buildQueryString(params);
    const url = `/api/v1/locations${searchParams ? `?${searchParams}` : ''}`;
    return this.request(url);
  }

  async getLocation(id: string): Promise<ApiResponse<any>> {
    return this.request(`/api/v1/locations/${id}`);
  }

  async createLocation(data: any): Promise<ApiResponse<any>> {
    return this.request('/api/v1/locations', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateLocation(id: string, data: any): Promise<ApiResponse<any>> {
    return this.request(`/api/v1/locations/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  async deleteLocation(id: string): Promise<ApiResponse<any> | null> {
    return this.request(`/api/v1/locations/${id}`, {
      method: 'DELETE',
    });
  }

  // Location QR API
  async getLocationQR(id: string, format?: string, size?: number): Promise<any> {
    const params: Record<string, string> = {};
    if (format) params.format = format;
    if (size) params.size = String(size);

    const searchParams = this.buildQueryString(params);
    return this.request(`/api/v1/locations/${id}/qr${searchParams ? `?${searchParams}` : ''}`);
  }

  async getLocationStatistics(id: string): Promise<ApiResponse<any>> {
    return this.request(`/api/v1/locations/${id}/statistics`);
  }

  // Employee Locations API
  async getLocationEmployees(locationId: string): Promise<ApiResponse<any[]>> {
    return this.request(`/api/v1/employee-locations/location/${locationId}`);
  }

  async getEmployeeLocations(employeeId: string): Promise<ApiResponse<any[]>> {
    return this.request(`/api/v1/employee-locations/employee/${employeeId}`);
  }

  async assignEmployees(data: any): Promise<ApiResponse<any>> {
    return this.request('/api/v1/employee-locations/assign', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async unassignEmployees(data: any): Promise<ApiResponse<any>> {
    return this.request('/api/v1/employee-locations/unassign', {
      method: 'DELETE',
      body: JSON.stringify(data),
    });
  }

  async updateAccessLevel(id: string, data: any): Promise<ApiResponse<any>> {
    return this.request(`/api/v1/employee-locations/${id}/access`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  async checkAccess(employeeId: string, locationId: string): Promise<ApiResponse<any>> {
    return this.request(`/api/v1/employee-locations/check-access/${employeeId}/${locationId}`);
  }

  // Time Records API
  async getTimeRecords(params?: Record<string, any>): Promise<PaginatedResponse<any>> {
    const searchParams = this.buildQueryString(params);
    return this.request(`/api/v1/time-records${searchParams ? `?${searchParams}` : ''}`);
  }

  async getTimeRecordsByEmployee(employeeId: string, params?: Record<string, any>): Promise<PaginatedResponse<any>> {
    const searchParams = this.buildQueryString({ employeeId, ...params });
    return this.request(`/api/v1/time-records/by-employee${searchParams ? `?${searchParams}` : ''}`);
  }

  async aggregateTimeRecords(params: Record<string, any>): Promise<any> {
    const searchParams = this.buildQueryString(params);
    return this.request(`/api/v1/time-records/aggregate${searchParams ? `?${searchParams}` : ''}`);
  }

  async getMyTimeRecords(params?: Record<string, any>): Promise<PaginatedResponse<any>> {
    const searchParams = this.buildQueryString(params);
    return this.request(`/api/v1/time-records/my-records${searchParams ? `?${searchParams}` : ''}`);
  }

  async getMySummary(params?: Record<string, any>): Promise<any> {
    const searchParams = this.buildQueryString(params);
    return this.request(`/api/v1/time-records/my-summary${searchParams ? `?${searchParams}` : ''}`);
  }

  async generateTimeRecords(data: {
    start_date: string;
    end_date: string;
    employee_id?: string;
    employee_ids?: string[];
    overwrite?: boolean;
  }): Promise<{
    generated_count: number;
    updated_count: number;
    employees_processed: number;
    errors?: string[];
  }> {
    return this.request('/api/v1/time-records/generate', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  // Attendance Events API
  async getAttendanceEvents(params?: Record<string, any>): Promise<PaginatedResponse<any>> {
    const searchParams = this.buildQueryString(params);
    return this.request(`/api/v1/attendance-events${searchParams ? `?${searchParams}` : ''}`);
  }

  async createAttendanceEvent(data: any): Promise<ApiResponse<any>> {
    return this.request('/api/v1/attendance-events', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  // Sync Status API
  async getSyncStatus(syncType?: string): Promise<ApiResponse<any[]>> {
    const params: Record<string, string> = {};
    if (syncType) {
      params.sync_type = syncType;
    }
    const searchParams = this.buildQueryString(params);
    return this.request(`/api/v1/1c/sync-status${searchParams ? `?${searchParams}` : ''}`);
  }

  async getLatestSync(): Promise<ApiResponse<any>> {
    return this.request('/api/v1/1c/sync-status/latest');
  }

  /**
   * 1C Integration - Sync directories
   * Запускает полную синхронизацию справочников из 1С
   */
  async sync1CDirectories(): Promise<OneCSync_Response> {
    return this.request<OneCSync_Response>('/api/v1/1c/sync', {
      method: 'GET',
    });
  }

  // Schedule Templates API
  async get(endpoint: string, config?: { params?: Record<string, any> }): Promise<any> {
    const searchParams = this.buildQueryString(config?.params);
    return this.request(`${endpoint}${searchParams ? `?${searchParams}` : ''}`);
  }

  async post(endpoint: string, data?: any): Promise<any> {
    return this.request(endpoint, {
      method: 'POST',
      body: data ? JSON.stringify(data) : undefined,
    });
  }

  async patch(endpoint: string, data?: any): Promise<any> {
    return this.request(endpoint, {
      method: 'PATCH',
      body: data ? JSON.stringify(data) : undefined,
    });
  }

  async put(endpoint: string, data?: any): Promise<any> {
    return this.request(endpoint, {
      method: 'PUT',
      body: data ? JSON.stringify(data) : undefined,
    });
  }

  async delete(endpoint: string): Promise<any> {
    return this.request(endpoint, {
      method: 'DELETE',
    });
  }

  // Tenant Settings API
  async getOrganizationInfo(): Promise<any> {
    return this.request('/api/v1/tenants/me/organization');
  }

  async getTenantSettings(): Promise<any> {
    return this.request('/api/v1/tenants/me/settings');
  }

  async updateTenantSettings(data: any): Promise<any> {
    return this.request('/api/v1/tenants/me/settings', {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  async generateQRSecret(): Promise<any> {
    return this.request('/api/v1/tenants/me/settings/qr-secret', {
      method: 'POST',
    });
  }

  // ==================== NOTIFICATIONS API ====================

  // Device Tokens API
  async registerDeviceToken(data: any): Promise<any> {
    return this.request('/api/v1/device-tokens/register', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async unregisterDeviceToken(deviceId: string): Promise<any> {
    return this.request('/api/v1/device-tokens/unregister', {
      method: 'DELETE',
      body: JSON.stringify({ device_id: deviceId }),
    });
  }

  async getMyDevices(): Promise<ApiResponse<any[]>> {
    return this.request('/api/v1/device-tokens/my-devices');
  }

  // Notifications API
  async getNotifications(params?: Record<string, any>): Promise<PaginatedResponse<any>> {
    const searchParams = this.buildQueryString(params);
    return this.request(`/api/v1/notifications${searchParams ? `?${searchParams}` : ''}`);
  }

  async getNotification(id: string): Promise<ApiResponse<any>> {
    return this.request(`/api/v1/notifications/${id}`);
  }

  async createNotification(data: any): Promise<any> {
    return this.request('/api/v1/notifications', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async uploadNotificationAttachment(
    file: File,
  ): Promise<{ filename: string; url: string; size?: number; content_type?: string }> {
    const token = await this.authService.getValidAccessToken();
    const tenantId = this.authService.getTenantId();

    const formData = new FormData();
    formData.append('file', file);

    const response = await fetch(`${this.baseURL}/api/v1/notifications/upload-attachment`, {
      method: 'POST',
      headers: {
        ...(token && { 'Authorization': `Bearer ${token}` }),
        ...(tenantId && { 'X-Tenant-Id': tenantId }),
      },
      body: formData,
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ message: 'Upload failed' }));
      throw new Error(error.message || `Upload failed (${response.status})`);
    }

    return response.json();
  }

  async updateNotification(id: string, data: any): Promise<ApiResponse<any>> {
    return this.request(`/api/v1/notifications/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  async deleteNotification(id: string): Promise<ApiResponse<any> | null> {
    return this.request(`/api/v1/notifications/${id}`, {
      method: 'DELETE',
    });
  }

  async sendNotification(id: string): Promise<any> {
    return this.request(`/api/v1/notifications/${id}/send`, {
      method: 'POST',
    });
  }

  async getMyNotifications(params?: Record<string, any>): Promise<PaginatedResponse<any>> {
    const searchParams = this.buildQueryString(params);
    return this.request(`/api/v1/notifications/my-notifications${searchParams ? `?${searchParams}` : ''}`);
  }

  async markNotificationAsRead(id: string): Promise<any> {
    return this.request(`/api/v1/notifications/${id}/mark-as-read`, {
      method: 'PATCH',
    });
  }

  async markAllNotificationsAsRead(): Promise<any> {
    return this.request('/api/v1/notifications/mark-all-as-read', {
      method: 'PATCH',
    });
  }

  // Бэкенд возвращает объект статистики напрямую (не в обёртке { data }).
  async getNotificationDeliveryStats(id: string): Promise<any> {
    return this.request(`/api/v1/notifications/${id}/delivery-stats`);
  }

  // ==================== ORGANIZATIONS API ====================

  async getOrganizations(params?: Record<string, any>): Promise<PaginatedResponse<any>> {
    // Transform sortBy + sortOrder into sort (backend expects 'name:asc' format)
    const transformedParams = { ...params };
    if (transformedParams?.sortBy) {
      transformedParams.sort = `${transformedParams.sortBy}:${transformedParams.sortOrder || 'asc'}`;
      delete transformedParams.sortBy;
      delete transformedParams.sortOrder;
    }
    const searchParams = this.buildQueryString(transformedParams);
    return this.request(`/api/v1/organizations${searchParams ? `?${searchParams}` : ''}`);
  }

  async getOrganization(id: string): Promise<any> {
    return this.request(`/api/v1/organizations/${id}`);
  }

  async getOrganizationWithStats(id: string): Promise<any> {
    return this.request(`/api/v1/organizations/${id}/with-stats`);
  }

  async getOrganizationStats(id: string): Promise<any> {
    return this.request(`/api/v1/organizations/${id}/stats`);
  }

  async getOrganizationEmployees(id: string, params?: Record<string, any>): Promise<PaginatedResponse<any>> {
    const searchParams = this.buildQueryString(params);
    return this.request(`/api/v1/organizations/${id}/employees${searchParams ? `?${searchParams}` : ''}`);
  }

  async getHeadquartersOrganization(): Promise<any> {
    return this.request('/api/v1/organizations/headquarters');
  }

  async createOrganization(data: any): Promise<any> {
    return this.request('/api/v1/organizations', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateOrganization(id: string, data: any): Promise<any> {
    return this.request(`/api/v1/organizations/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  async deleteOrganization(id: string): Promise<any> {
    return this.request(`/api/v1/organizations/${id}`, {
      method: 'DELETE',
    });
  }

  // Documents API (Admin endpoints - with employeeId) - POST requests for document generation
  async getPayslipDocument(employeeId: string, period: string): Promise<any> {
    return this.request(`/api/v1/documents/admin/payslip?employeeId=${employeeId}&period=${period}`, {
      method: 'POST',
    });
  }

  async getOrderDocument(employeeId: string, documentId: string): Promise<any> {
    return this.request(`/api/v1/documents/admin/order?employeeId=${employeeId}&documentId=${documentId}`, {
      method: 'POST',
    });
  }

  async getCertificateDocument(employeeId: string, documentId: string): Promise<any> {
    return this.request(`/api/v1/documents/admin/certificate?employeeId=${employeeId}&documentId=${documentId}`, {
      method: 'POST',
    });
  }

  async getContractDocument(employeeId: string): Promise<any> {
    return this.request(`/api/v1/documents/admin/contract?employeeId=${employeeId}`, {
      method: 'POST',
    });
  }

  async getMyDocuments(params?: Record<string, any>): Promise<PaginatedResponse<any>> {
    const searchParams = this.buildQueryString(params);
    return this.request(`/api/v1/documents/my-documents${searchParams ? `?${searchParams}` : ''}`);
  }

  // [ADMIN] Documents API - для администраторов
  async getAllDocumentsAdmin(params?: Record<string, any>): Promise<any> {
    const searchParams = this.buildQueryString(params);
    return this.request(`/api/v1/documents/admin/all-documents${searchParams ? `?${searchParams}` : ''}`);
  }

  async downloadDocumentAdmin(documentId: string): Promise<{ url: string; filename: string; expiresIn: number }> {
    // Получаем signed URL из backend
    return this.request(`/api/v1/documents/admin/documents/${documentId}/download`);
  }

  async uploadDocument(formData: FormData): Promise<any> {
    const token = await this.authService.getValidAccessToken();
    const tenantId = this.authService.getTenantId();

    const response = await fetch(`${this.baseURL}/api/v1/documents/admin/upload`, {
      method: 'POST',
      headers: {
        ...(token && { 'Authorization': `Bearer ${token}` }),
        ...(tenantId && { 'X-Tenant-Id': tenantId }),
      },
      body: formData,
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ message: 'Upload failed' }));
      throw new Error(error.message || `Upload failed (${response.status})`);
    }

    return response.json();
  }

  // ==================== REPORTS API ====================

  async getLatenessReport(params: { date: string; organizationId?: string; departmentId?: string }): Promise<any> {
    const searchParams = this.buildQueryString(params);
    return this.request(`/api/v1/reports/lateness${searchParams ? `?${searchParams}` : ''}`);
  }

  async getNoScheduleReport(params: { organizationId?: string; departmentId?: string }): Promise<any> {
    const searchParams = this.buildQueryString(params);
    return this.request(`/api/v1/reports/no-schedule${searchParams ? `?${searchParams}` : ''}`);
  }

  async getTimesheetReport(params: { year: number; month: number; organizationId?: string; departmentId?: string }): Promise<any> {
    const searchParams = this.buildQueryString(params);
    return this.request(`/api/v1/reports/timesheet${searchParams ? `?${searchParams}` : ''}`);
  }

  async getTalentReport(params: { organizationId?: string; departmentId?: string; asOf?: string }): Promise<any> {
    const searchParams = this.buildQueryString(params);
    return this.request(`/api/v1/reports/talent${searchParams ? `?${searchParams}` : ''}`);
  }

  async getSalariesReport(params: { period: string; organizationId?: string; departmentId?: string }): Promise<any> {
    const searchParams = this.buildQueryString(params);
    return this.request(`/api/v1/reports/salaries${searchParams ? `?${searchParams}` : ''}`);
  }

  async getRevenueVsPayrollReport(params: { locationId: string; year: number; month: number; departmentIds?: string }): Promise<any> {
    const searchParams = this.buildQueryString(params);
    return this.request(`/api/v1/reports/revenue-vs-payroll${searchParams ? `?${searchParams}` : ''}`);
  }

  async getWaiterExcellenceReport(params: { organizationId?: string; from: string; to: string; topN?: number; minChecks?: number }): Promise<any> {
    const searchParams = this.buildQueryString(params);
    return this.request(`/api/v1/reports/waiter-excellence${searchParams ? `?${searchParams}` : ''}`);
  }

  async getScheduleEffectivenessReport(params: { from: string; to: string; organizationId?: string; departmentId?: string }): Promise<any> {
    const searchParams = this.buildQueryString(params);
    return this.request(`/api/v1/reports/schedule-effectiveness${searchParams ? `?${searchParams}` : ''}`);
  }

  // ==================== STAFFING LIMITS API ====================

  // Legacy endpoint (backward compatibility)
  async getStaffingLimits(params?: Record<string, any>): Promise<PaginatedResponse<any>> {
    const searchParams = this.buildQueryString(params);
    return this.request(`/api/v1/staffing-limits${searchParams ? `?${searchParams}` : ''}`);
  }

  async getStaffingLimit(id: string): Promise<ApiResponse<any>> {
    return this.request(`/api/v1/staffing-limits/${id}`);
  }

  // Legacy create (backward compatibility)
  async createStaffingLimit(data: any): Promise<any> {
    return this.request('/api/v1/staffing-limits', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateStaffingLimit(id: string, data: any): Promise<any> {
    return this.request(`/api/v1/staffing-limits/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  async deleteStaffingLimit(id: string): Promise<any> {
    return this.request(`/api/v1/staffing-limits/${id}`, {
      method: 'DELETE',
    });
  }

  // ==================== DAILY STAFFING LIMITS API ====================

  async getDailyStaffingLimits(params?: Record<string, any>): Promise<PaginatedResponse<any>> {
    const searchParams = this.buildQueryString(params);
    return this.request(`/api/v1/staffing-limits/daily${searchParams ? `?${searchParams}` : ''}`);
  }

  async createDailyStaffingLimit(data: any): Promise<any> {
    return this.request('/api/v1/staffing-limits/daily', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateDailyStaffingLimit(id: string, data: any): Promise<any> {
    return this.request(`/api/v1/staffing-limits/daily/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  async checkDailyStaffingLimit(params: {
    date: string;
    positionId: string;
    departmentId?: string;
    organizationId?: string;
  }): Promise<any> {
    const searchParams = this.buildQueryString(params);
    return this.request(`/api/v1/staffing-limits/check/daily${searchParams ? `?${searchParams}` : ''}`);
  }

  // ==================== MONTHLY STAFFING LIMITS API ====================

  async getMonthlyStaffingLimits(params?: Record<string, any>): Promise<PaginatedResponse<any>> {
    const searchParams = this.buildQueryString(params);
    return this.request(`/api/v1/staffing-limits/monthly${searchParams ? `?${searchParams}` : ''}`);
  }

  async createMonthlyStaffingLimit(data: any): Promise<any> {
    return this.request('/api/v1/staffing-limits/monthly', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateMonthlyStaffingLimit(id: string, data: any): Promise<any> {
    return this.request(`/api/v1/staffing-limits/monthly/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  async checkMonthlyStaffingLimit(params: {
    year: number;
    month: number;
    positionId: string;
    departmentId?: string;
    organizationId?: string;
    additionalMinutes?: number;
  }): Promise<any> {
    const searchParams = this.buildQueryString(params);
    return this.request(`/api/v1/staffing-limits/check/monthly${searchParams ? `?${searchParams}` : ''}`);
  }

  // ==================== GENERATE MONTHLY LIMITS API ====================

  async previewMonthlyStaffingLimits(params: {
    organization_id: string;
    year: number;
    month: number;
  }): Promise<any> {
    return this.request('/api/v1/staffing-limits/monthly/preview', {
      method: 'POST',
      body: JSON.stringify(params),
    });
  }

  async generateMonthlyStaffingLimits(data: {
    organization_id: string;
    year: number;
    month: number;
    limits: Array<{ position_id: string; max_hours: number }>;
  }): Promise<any> {
    return this.request('/api/v1/staffing-limits/monthly/generate', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  // ==================== SHIFT LIMITS (из штатки 1С) ====================

  async getShiftLimits(params?: Record<string, any>): Promise<PaginatedResponse<any>> {
    const searchParams = this.buildQueryString(params);
    return this.request(`/api/v1/staffing-limits/shifts${searchParams ? `?${searchParams}` : ''}`);
  }

  async generateShiftLimitsFromStaffing(data: {
    year: number;
    month: number;
    overwrite_manual?: boolean;
  }): Promise<any> {
    return this.request('/api/v1/staffing-limits/monthly/generate-from-staffing', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async checkExceptionShiftLimit(params: {
    employee_id: string;
    date: string;
    is_working_day?: boolean;
    is_replacement?: boolean;
    replacement_employee_id?: string;
  }): Promise<any> {
    const searchParams = this.buildQueryString(params);
    return this.request(`/api/v1/schedule-exceptions/check-shift-limit${searchParams ? `?${searchParams}` : ''}`);
  }

  // ==================== STAFFING SCHEDULE API ====================

  async getStaffingSchedule(params?: Record<string, any>): Promise<PaginatedResponse<any>> {
    const searchParams = this.buildQueryString(params);
    return this.request(`/api/v1/staffing-schedule${searchParams ? `?${searchParams}` : ''}`);
  }

  // ==================== TESTING API ====================

  // Tests
  async getTests(params?: Record<string, any>): Promise<PaginatedResponse<any>> {
    const searchParams = this.buildQueryString(params);
    return this.request(`/api/v1/testing/tests${searchParams ? `?${searchParams}` : ''}`);
  }

  async getTest(id: string): Promise<any> {
    return this.request(`/api/v1/testing/tests/${id}`);
  }

  async createTest(data: any): Promise<any> {
    return this.request('/api/v1/testing/tests', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateTest(id: string, data: any): Promise<any> {
    return this.request(`/api/v1/testing/tests/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  async deleteTest(id: string): Promise<any> {
    return this.request(`/api/v1/testing/tests/${id}`, {
      method: 'DELETE',
    });
  }

  // Test Questions
  async getTestQuestions(testId: string): Promise<any> {
    return this.request(`/api/v1/testing/tests/${testId}/questions`);
  }

  async createTestQuestion(testId: string, data: any): Promise<any> {
    return this.request(`/api/v1/testing/tests/${testId}/questions`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateTestQuestion(testId: string, questionId: string, data: any): Promise<any> {
    return this.request(`/api/v1/testing/tests/${testId}/questions/${questionId}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  async deleteTestQuestion(testId: string, questionId: string): Promise<any> {
    return this.request(`/api/v1/testing/tests/${testId}/questions/${questionId}`, {
      method: 'DELETE',
    });
  }

  // Test Assignments
  async getTestAssignments(params?: Record<string, any>): Promise<PaginatedResponse<any>> {
    const searchParams = this.buildQueryString(params);
    return this.request(`/api/v1/testing/assignments${searchParams ? `?${searchParams}` : ''}`);
  }

  async createTestAssignment(data: any): Promise<any> {
    return this.request('/api/v1/testing/assignments', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async deleteTestAssignment(id: string): Promise<any> {
    return this.request(`/api/v1/testing/assignments/${id}`, {
      method: 'DELETE',
    });
  }

  // Test Results
  async getTestResults(params?: Record<string, any>): Promise<PaginatedResponse<any>> {
    const searchParams = this.buildQueryString(params);
    return this.request(`/api/v1/testing/results${searchParams ? `?${searchParams}` : ''}`);
  }

  async getTestResultsSummary(): Promise<any> {
    return this.request('/api/v1/testing/results/summary');
  }

  // ==================== My Testing (Employee) ====================

  async getMyTests(): Promise<any> {
    return this.request('/api/v1/testing/my/tests');
  }

  async startMyTestAttempt(testId: string): Promise<any> {
    return this.request(`/api/v1/testing/my/tests/${testId}/start`, { method: 'POST' });
  }

  async getMyAttempt(attemptId: string): Promise<any> {
    return this.request(`/api/v1/testing/my/attempts/${attemptId}`);
  }

  async submitMyAnswer(attemptId: string, data: { question_id: string; selected_options: string[] }): Promise<any> {
    return this.request(`/api/v1/testing/my/attempts/${attemptId}/answer`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async finishMyAttempt(attemptId: string): Promise<any> {
    return this.request(`/api/v1/testing/my/attempts/${attemptId}/finish`, { method: 'POST' });
  }

  // ==================== FEEDBACK API ====================

  async getFeedback(params?: Record<string, any>): Promise<PaginatedResponse<any>> {
    const searchParams = this.buildQueryString(params);
    return this.request(`/api/v1/feedback${searchParams ? `?${searchParams}` : ''}`);
  }

  async getFeedbackById(id: string): Promise<any> {
    return this.request(`/api/v1/feedback/${id}`);
  }

  async updateFeedbackStatus(id: string, data: { status: string; admin_comment?: string }): Promise<any> {
    return this.request(`/api/v1/feedback/${id}/status`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  async getMyFeedback(params?: Record<string, any>): Promise<PaginatedResponse<any>> {
    const searchParams = this.buildQueryString(params);
    return this.request(`/api/v1/feedback/my${searchParams ? `?${searchParams}` : ''}`);
  }

  async createFeedback(data: any): Promise<any> {
    return this.request('/api/v1/feedback', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }
  // ==================== Gamification ====================

  async getBadges(params?: Record<string, any>): Promise<PaginatedResponse<any>> {
    const searchParams = this.buildQueryString(params);
    return this.request(`/api/v1/gamification/badges${searchParams ? `?${searchParams}` : ''}`);
  }

  async getBadge(id: string): Promise<any> {
    return this.request(`/api/v1/gamification/badges/${id}`);
  }

  async createBadge(data: any): Promise<any> {
    return this.request('/api/v1/gamification/badges', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateBadge(id: string, data: any): Promise<any> {
    return this.request(`/api/v1/gamification/badges/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  async deleteBadge(id: string): Promise<any> {
    return this.request(`/api/v1/gamification/badges/${id}`, {
      method: 'DELETE',
    });
  }

  async awardBadge(badgeId: string, data: { employee_ids: string[] }): Promise<any> {
    return this.request(`/api/v1/gamification/badges/${badgeId}/award`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async getEmployeeXpList(params?: Record<string, any>): Promise<PaginatedResponse<any>> {
    const searchParams = this.buildQueryString(params);
    return this.request(`/api/v1/gamification/xp${searchParams ? `?${searchParams}` : ''}`);
  }

  async getEmployeeXpDetail(employeeId: string): Promise<any> {
    return this.request(`/api/v1/gamification/xp/${employeeId}`);
  }

  async getXpTransactions(employeeId: string, params?: Record<string, any>): Promise<PaginatedResponse<any>> {
    const searchParams = this.buildQueryString(params);
    return this.request(`/api/v1/gamification/xp/${employeeId}/transactions${searchParams ? `?${searchParams}` : ''}`);
  }

  async awardXp(data: { employee_id: string; amount: number; description?: string }): Promise<any> {
    return this.request('/api/v1/gamification/xp/award', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async getLeaderboard(params?: Record<string, any>): Promise<any[]> {
    const searchParams = this.buildQueryString(params);
    return this.request(`/api/v1/gamification/leaderboard${searchParams ? `?${searchParams}` : ''}`);
  }

  async getMyLeaderboardRank(): Promise<any> {
    return this.request('/api/v1/gamification/leaderboard/my');
  }

  async getMyGamificationProfile(): Promise<any> {
    return this.request('/api/v1/gamification/my/profile');
  }

  async getMyBadges(params?: Record<string, any>): Promise<PaginatedResponse<any>> {
    const searchParams = this.buildQueryString(params);
    return this.request(`/api/v1/gamification/my/badges${searchParams ? `?${searchParams}` : ''}`);
  }

  async getMyXpHistory(params?: Record<string, any>): Promise<PaginatedResponse<any>> {
    const searchParams = this.buildQueryString(params);
    return this.request(`/api/v1/gamification/my/xp-history${searchParams ? `?${searchParams}` : ''}`);
  }

  async getGamificationSettings(): Promise<any> {
    return this.request('/api/v1/gamification/settings');
  }

  async updateGamificationSettings(data: any): Promise<any> {
    return this.request('/api/v1/gamification/settings', {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  // ==================== NEWS API ====================

  async getNews(params?: Record<string, any>): Promise<PaginatedResponse<any>> {
    const searchParams = this.buildQueryString(params);
    return this.request(`/api/v1/news${searchParams ? `?${searchParams}` : ''}`);
  }

  async getNewsById(id: string): Promise<any> {
    return this.request(`/api/v1/news/${id}`);
  }

  async createNews(data: any): Promise<any> {
    return this.request('/api/v1/news', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateNews(id: string, data: any): Promise<any> {
    return this.request(`/api/v1/news/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  async deleteNews(id: string): Promise<void> {
    return this.request(`/api/v1/news/${id}`, { method: 'DELETE' });
  }

  async uploadNewsImage(file: File): Promise<{ url: string; key: string }> {
    const token = await this.authService.getValidAccessToken();
    const tenantId = this.authService.getTenantId();

    const formData = new FormData();
    formData.append('file', file);

    const response = await fetch(`${this.baseURL}/api/v1/news/images/upload`, {
      method: 'POST',
      headers: {
        ...(token && { 'Authorization': `Bearer ${token}` }),
        ...(tenantId && { 'X-Tenant-Id': tenantId }),
      },
      body: formData,
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ message: 'Upload failed' }));
      throw new Error(error.message || `Upload failed (${response.status})`);
    }

    return response.json();
  }
}

export const apiClient = new ApiClient();
export const api = apiClient;